<template>
  <div class="studio-product-checkout">
    <div class="row">
      <div
        class="offset-md-2 offset-lg-2 col-12 col-md-8 col-lg-5 mt-2 mt-lg-4 p-lg-0"
      >
        <slot name="breadcrumbs" :product="product" v-if="product"></slot>
        <div class="loading w-100 text-center mt-4" v-if="loading">
          <HMSpinner />
        </div>
        <div v-if="product && !isCheckoutAllowed" class="mt-5">
          <h2>
            {{ $t('PRODUCTS.TEXT.ENROLLMENT_CLOSED') }}
          </h2>
          <span @click="$router.back()" class="pointer c-brand-color">
            {{ $t('GENERAL.BUTTON.GO_BACK') }}
          </span>
        </div>
        <div v-if="product && isCheckoutAllowed">
          <div
            class="mobile-product-container d-block d-lg-none mt-3 bg-lightest-gray p-3"
          >
            <div
              class="d-flex flex-column flex-sm-row justify-content-start align-items-start flex-nowrap"
            >
              <HMFixedAspectRatioContainer class="img-container mobile">
                <ProductThumbnail :product="product" :hideOverlay="true" />
              </HMFixedAspectRatioContainer>
              <div
                class="d-flex flex-column justify-content-start align-items-start ms-sm-2 mt-2"
              >
                <span class="c-charcoal">{{ product.product_name }}</span>
                <span
                  class="smaller mt-2"
                  :class="{
                    'price-line-through': wishPriceSelected,
                  }"
                  v-if="getSelectedPriceOption"
                  v-html="
                    getSelectedPriceOption.selectedTitle ||
                    getSelectedPriceOption.html
                  "
                ></span>
              </div>
            </div>
            <div v-if="hasTrial" class="">
              <hr class="mt-3" />
              <div class="mt-2">
                <span v-html="sanitize(getFreeTrialHeading)"></span>
              </div>
            </div>
          </div>
          <h2 class="my-4">{{ $t('PRODUCTS.LABELS.TYPE_OF_PURCHASE') }}</h2>
          <p v-if="checkoutSubtitle" class="checkout-subtitle">
            {{ checkoutSubtitle }}
          </p>
          <div class="price-options">
            <div
              class="price-option-container"
              v-for="option in priceOptions"
              :key="option.value"
            >
              <HMRadio
                class="price-option"
                v-model="selectedPriceOption"
                group="price-options"
                :name="option.value"
                @update:modelValue="priceOptionChanged"
              >
                <span v-if="option.text">{{ option.text }}</span>
                <span v-if="option.html" v-html="sanitize(option.html)"></span>
              </HMRadio>
              <div
                class="d-flex mt-3 align-items-start"
                v-if="
                  product.wish_price_enabled &&
                  !getSelectedPriceOption.disabledWishPrice &&
                  selectedPriceOption === option.value
                "
              >
                <div class="w-100">
                  <HMTextInput
                    type="number"
                    class="m-0 wish-price"
                    v-model="wishPrice"
                    :rules="`min_currency:${option.wishPriceAmount},${option.wishPrice}`"
                    :placeholder="$t('PRODUCTS.LABELS.PAY_WHAT_YOU_WISH')"
                    name="wish_price"
                    :label="$t('PRODUCTS.LABELS.PAY_WHAT_YOU_WISH')"
                  >
                    <template v-slot:prepend>{{
                      productForPurchase.currency || 'USD'
                    }}</template>
                  </HMTextInput>
                </div>
                <HMButton
                  class="ms-md-3 ms-1"
                  color="brand"
                  @click="applyWishPrice"
                  :disabled="!canApplyWishPrice"
                >
                  {{ $t('SIGNUP.BUTTON.APPLY') }}
                </HMButton>
              </div>
            </div>
          </div>
          <hr class="mt-4" />
          <div v-if="isLoggedIn && user" class="currently-logged-in mt-4">
            <HMUserSection
              @click="logout"
              :title="$t('GENERAL.LABEL.LOGGED_IN_AS')"
              :subtitle="user.user_full_name"
              :actionText="$t('GENERAL.LABEL.LOG_OUT_CHOOSE_DIFFERENT_ACCOUNT')"
            />
          </div>
          <div v-else class="mt-4 row log-in-or-register">
            <div class="col-12 col-md-6">
              <HMUserSection
                @click="openLoginModal"
                :title="$t('PRODUCTS.LABELS.LOG_IN')"
                :subtitle="$t('GENERAL.LABEL.ALREADY_HAVE_AN_ACCOUNT')"
                :actionText="$t('GENERAL.BUTTON.LOG_IN')"
              />
            </div>
            <div class="col-12 col-md-6">
              <HMUserSection
                @click="openRegisterModal"
                :title="$t('GENERAL.LABEL.REGISTRATION')"
                :subtitle="
                  $t('PRODUCTS.LABELS.CREATE_NEW_ACCOUNT_AND_CONTINUE')
                "
                :actionText="$t('GENERAL.LABEL.CREATE_AN_ACCOUNT')"
              />
            </div>
          </div>
          <div class="billing-information-container mt-4">
            <div
              v-if="isStripeProcessor && existingCreditCard"
              v-show="isLoggedIn && !isForeverFree"
            >
              <hr class="mt-4" />
              <h2>
                {{ $t('PRODUCTS.LABELS.BILLING_INFORMATION') }}
              </h2>
              <div
                v-if="existingCreditCard"
                class="form-group mb-0 d-flex flex-row justify-content-start align-items-center"
              >
                <span class="me-3">
                  {{ $t('PRODUCTS.LABELS.USE_EXISTING_CARD') }}
                </span>
                <HMSwitch v-model="useExistingCustomerData" />
              </div>
              <div
                v-show="useExistingCustomerData"
                v-html="existingCreditCardText"
              ></div>
            </div>
            <div
              class="d-block d-lg-none form-group mb-3"
              v-if="canShowCouponCode"
            >
              <label for="coupon" class="form-label">
                {{ $t('SIGNUP.LABEL.COUPON_CODE') }}
              </label>
              <div class="d-flex" :class="{ 'is-invalid': couponError }">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-valid': coupon }"
                  name="coupon"
                  id="coupon"
                  v-model.trim="couponCode"
                />
                <span
                  class="glyphicon glyphicon-ok form-control-feedback"
                  aria-hidden="true"
                ></span>
                <HMButton
                  class="ms-4"
                  :disabled="!couponCode"
                  color="outline-light-gray"
                  @click="applyCoupon"
                  :loading="isCheckingCoupon"
                >
                  {{ $t('SIGNUP.BUTTON.APPLY') }}
                </HMButton>
              </div>
              <span class="invalid-feedback" v-if="couponError">
                {{ couponError }}
              </span>
            </div>
            <hr class="my-4" />
            <div
              class="summary-container border rounded d-block d-lg-none"
              v-if="getSelectedPriceOption"
            >
              <div class="summary-section">
                <h4 class="py-3 m-0">{{ $t('PRODUCTS.LABELS.SUMMARY') }}</h4>
              </div>
              <hr class="p-0 m-0" />
              <div class="summary-section trial" v-if="hasTrial">
                <span v-html="sanitize(getFreeTrialHeading)"></span>
              </div>
              <hr class="p-0 m-0" v-if="hasTrial" />
              <div
                class="summary-section coupon"
                v-if="coupon && getSelectedPriceOption.isDiscountable"
              >
                <div v-html="sanitize(getCheckoutDiscountText)"></div>
                <div v-html="sanitize(discountDurationValidForText)"></div>
              </div>
              <hr class="p-0 m-0" v-if="coupon" />
              <div
                v-if="getSelectedPriceOption.tax"
                class="summary-section tax-info d-flex flex-row justify-content-between align-items-start py-3"
              >
                <span>{{ product.tax_rate_name }}</span>
                <span class="c-charcoal">
                  {{ getTaxText }}
                </span>
              </div>
              <hr v-if="getSelectedPriceOption.tax" class="mt-0" />
              <div
                class="summary-section total d-flex flex-row justify-content-between align-items-start flex-wrap"
              >
                <span>{{ $t('PRODUCTS.LABELS.TOTAL') }}:</span>
                <div class="d-flex flex-column text-end">
                  <div v-if="hasTrial">
                    {{ getCheckoutTrialText }}
                  </div>
                  <div
                    class="h2 c-charcoal m-0"
                    v-html="sanitize(getPriceTranslation)"
                  ></div>
                  <div
                    v-if="getCheckoutNonDiscountedPriceThereafterText"
                    class="small mt-2"
                    v-html="
                      sanitize(getCheckoutNonDiscountedPriceThereafterText)
                    "
                  ></div>
                  <div
                    v-if="getSelectedPriceOption.isPaymentPlan"
                    class="small mt-2"
                    v-html="sanitize(getPaymentPlanThereafterText)"
                  ></div>
                  <div class="small mt-2 text-end">
                    {{
                      $t('STUDIO.TEXT.ALL_PRICES_ARE_IN_CURRENCY', {
                        currency: studio.currency || 'USD',
                      })
                    }}
                  </div>
                </div>
              </div>
              <div v-if="hasTrial" class="small c-charcoal mt-2 p-2">
                <i>{{
                  $t('PRODUCTS.LABELS.FREE_TRIAL_CANCELLATION_POLICY')
                }}</i>
              </div>
            </div>
            <Form ref="studentDataForm">
              <StudentCheckoutInformationForm
                class="mt-3 mt-md-0"
                v-if="isLoggedIn"
                v-model="studentData"
              />
              <div class="marketing-optin">
                <HMCheckBox
                  class="my-4"
                  v-if="
                    isLoggedIn && !optedInMarketingEmails && isSmtpConnected
                  "
                  v-model="optedIn"
                  @update:modelValue="event => optIntoMarketing(event)"
                >
                  {{ $t('STUDIO.LABELS.OPT_INTO_MARKETING_EMAILS') }}
                </HMCheckBox>
              </div>
              <div class="terms-optin" v-if="hasTermsAndConditions">
                <HMCheckBox class="my-4" v-model="agreedToTerms">
                  <span>
                    {{ $t('STUDIO.LABELS.I_AGREE_TO') }}
                    <a
                      class="c-brand-color"
                      @click.stop="openTermsAndConditionsModal"
                    >
                      {{ $t('STUDIO.LABELS.TERMS_AND_CONDITIONS') }}
                    </a>
                  </span>
                </HMCheckBox>
              </div>
              <div class="checkout-button-container mt-4">
                <div
                  v-if="isStripeProcessor || isForeverFree"
                  class="col-md-12 p-0"
                  v-tooltip="{
                    content: checkoutButtonDisabledText,
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  <HMButton
                    class="w-100"
                    color="brand"
                    type="submit"
                    :disabled="disabledCheckout || !studentDataValid"
                    :loading="inProgress"
                    @click="purchase"
                  >
                    <span class="w-100 text-center">{{
                      getPurchaseButtonText
                    }}</span>
                  </HMButton>
                </div>
                <div v-if="isPayPalProcessor && !isForeverFree">
                  <div class="mt20">
                    <form
                      name="_xclick"
                      :action="getPayPalCheckoutUrl"
                      method="post"
                    >
                      <input
                        type="hidden"
                        name="business"
                        :value="productForPurchase.paypal_email"
                      />
                      <input
                        type="hidden"
                        name="currency_code"
                        :value="productForPurchase.currency || 'USD'"
                      />
                      <input
                        type="hidden"
                        name="item_name"
                        :value="productForPurchase.product_name"
                      />
                      <input
                        type="hidden"
                        name="notify_url"
                        :value="getPayPalNotifyUrl"
                      />
                      <input
                        type="hidden"
                        name="return"
                        :value="getPayPalReturnUrl"
                      />
                      <input
                        type="hidden"
                        name="custom"
                        :value="getPayPalCustomParametersField"
                      />
                      <input type="hidden" name="no_shipping" value="1" />
                      <div v-if="!getSelectedPriceOption.isRecurring">
                        <input type="hidden" name="cmd" value="_xclick" />
                        <input type="hidden" name="amount" :value="getPrice" />
                      </div>
                      <div v-if="getSelectedPriceOption.isRecurring">
                        <div v-if="productForPurchase.trial">
                          <input type="hidden" name="a1" value="0" />
                          <input
                            type="hidden"
                            name="p1"
                            :value="productForPurchase.trial_units"
                          />
                          <input
                            type="hidden"
                            name="t1"
                            :value="productForPurchase.trial_period"
                          />
                        </div>
                        <input
                          type="hidden"
                          name="cmd"
                          value="_xclick-subscriptions"
                        />
                        <input
                          type="hidden"
                          name="a3"
                          :value="formatPayPalPrice(getPrice)"
                        />
                        <input
                          type="hidden"
                          name="p3"
                          :value="getSelectedPriceOption.billingInterval"
                        />
                        <input
                          type="hidden"
                          name="t3"
                          :value="getSelectedPriceOption.payPalPeriod"
                        />
                        <input type="hidden" name="src" value="1" />
                      </div>
                      <div
                        v-tooltip="{
                          content: checkoutButtonDisabledText,
                          delay: 100,
                          placement: 'top',
                          container: 'body',
                          trigger: 'click hover',
                        }"
                      >
                        <HMButton
                          class="w-100"
                          color="paypal"
                          type="submit"
                          :disabled="disabledCheckout || !studentDataValid"
                        >
                          <div class="w-100">
                            <span>{{
                              $t('PRODUCTS.LABELS.CHECK_OUT_WITH')
                            }}</span>
                            <img
                              src="/assets/images/PayPal.png"
                              class="ms-2 img-paypal"
                            />
                          </div>
                        </HMButton>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </Form>
          </div>
          <div class="mt-4 footer-info">
            <div class="d-block d-md-none">
              <hr class="mt-3" />
              <div
                class="d-flex d-md-none flex-row justify-content-between align-items-center flex-wrap"
              >
                <div
                  class="credit-cards d-flex flex-row justify-content-start flex-nowrap mb-2"
                >
                  <img
                    class="img-cc me-2"
                    src="/assets/images/Visa.png"
                  />
                  <img
                    class="img-cc me-2"
                    src="/assets/images/Amex.png"
                  />
                  <img
                    class="img-cc me-2"
                    src="/assets/images/Mastercard.png"
                  />
                  <img
                    class="img-cc mr-2"
                    src="/assets/images/cartes-bancaires.png"
                  />
                </div>
                <div class="d-flex">
                  <span>{{ $t('PRODUCTS.LABELS.CHECKOUT_BY') }}</span>
                  <img
                    v-if="isPayPalProcessor"
                    class="img-paypal ms-2"
                    src="/assets/images/PayPal.png"
                  />
                  <img
                    v-if="isStripeProcessor"
                    class="img-stripe ms-2"
                    src="/assets/images/Stripe.png"
                  />
                </div>
              </div>
              <hr class="mt-2" />
            </div>
            <div class="mt-4">
              <i 
                class="me-1 text-primary c-brand-color far fa-lock-alt"
              ></i>
              {{ $t('SIGNUP.TEXT.THIS_IS_A_SECURE_256_BIT') }}
              <span class="c-charcoal">{{ $t('SIGNUP.TEXT.100_SECURE') }}</span>
            </div>
            <div class="filler d-none d-sm-block"></div>
            <div
              class="d-none d-md-flex flex-row flex-wrap justify-content-between align-items-center mt-4"
            >
              <div
                class="credit-cards d-flex flex-row justify-content-start flex-nowrap"
              >
                <img
                  class="img-cc me-2"
                  src="/assets/images/Visa.png"
                />
                <img
                  class="img-cc me-2"
                  src="/assets/images/Amex.png"
                />
                <img
                  class="img-cc me-2"
                  src="/assets/images/Mastercard.png"
                />
                <img
                  class="img-cc mr-2"
                  src="/assets/images/cartes-bancaires.png"
                />
              </div>
              <div class="d-flex">
                <span>{{ $t('PRODUCTS.LABELS.CHECKOUT_BY') }}</span>
                <img
                  v-if="isPayPalProcessor"
                  class="img-paypal ms-2"
                  src="/assets/images/PayPal.png"
                />
                <img
                  v-if="isStripeProcessor"
                  class="img-stripe ms-2"
                  src="/assets/images/Stripe.png"
                />
              </div>
            </div>

            <div class="d-block d-lg-none">
              <hr class="mt-4" />
              <div
                class="d-flex flex-row justify-content-between align-items-center flex-wrap mt-3"
              >
                <h3 class="me-2">{{ $t('PRODUCTS.LABELS.NEED_ANY_HELP') }}</h3>
                <a
                  class="undecorated-router-link d-block"
                  :href="`mailto:${studio.email}`"
                >
                  <span>
                    <i class="fal fa-comments-alt c-brand-color"></i>
                    {{ $t('STUDIO.LABELS.CONTACT_US') }}
                  </span>
                </a>
              </div>
              <hr />
            </div>

            <span
              class="d-block smaller my-5"
              v-html="sanitize($t('SIGNUP.TEXT.THIS_SITE_IS_PROTECTED_BY'))"
            ></span>
          </div>
        </div>
      </div>
      <div
        class="checkout-summary-desktop d-none d-lg-block offset-lg-1 col-md-4 col-lg-4 bg-lightest-gray full-height"
        v-if="product && getSelectedPriceOption && isCheckoutAllowed"
      >
        <div class="position-fixed ms-2 ms-xl-4 me-2 ms-xl-4 h-100">
          <div
            class="d-flex flex-column justify-content-between align-items-start h-100"
          >
            <div class="top-section">
              <div
                class="product-section d-flex flex-row justify-content-start align-items-start flex-nowrap mt-4"
              >
                <HMFixedAspectRatioContainer class="img-container">
                  <ProductThumbnail :product="product" :hideOverlay="true" />
                </HMFixedAspectRatioContainer>
                <div
                  class="d-flex flex-column justify-content-start align-items-start ms-2 mt-2"
                >
                  <span class="c-charcoal">{{ product.product_name }}</span>
                  <span
                    class="smaller mt-2"
                    :class="{
                      'price-line-through': wishPriceSelected,
                    }"
                    v-html="
                      sanitize(
                        getSelectedPriceOption.selectedTitle ||
                          getSelectedPriceOption.html
                      )
                    "
                  ></span>
                </div>
              </div>
              <div v-if="hasTrial" class="trial-info text-end">
                <hr />
                <div class="my-2">
                  <span v-html="sanitize(getFreeTrialHeading)"></span>
                </div>
              </div>
              <hr />
              <div class="coupon-code">
                <div
                  class="form-group coupon-form mb-3"
                  v-if="canShowCouponCode"
                >
                  <label for="coupon"  class="form-label">
                    {{ $t('SIGNUP.LABEL.COUPON_CODE') }}
                  </label>
                  <div class="d-flex" :class="{ 'is-invalid': couponError }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-valid': coupon }"
                      name="coupon"
                      id="coupon"
                      v-model.trim="couponCode"
                    />
                    <span
                      class="glyphicon glyphicon-ok form-control-feedback"
                      aria-hidden="true"
                    ></span>
                    <HMButton
                      class="ms-4"
                      :disabled="!couponCode"
                      color="outline-light-gray"
                      @click="applyCoupon"
                      :loading="isCheckingCoupon"
                    >
                      {{ $t('SIGNUP.BUTTON.APPLY') }}
                    </HMButton>
                  </div>
                  <span class="invalid-feedback" v-if="couponError">
                    {{ couponError }}
                  </span>
                </div>
                <div
                  v-if="coupon && canShowCouponCode"
                  class="mt-1 coupon-info"
                >
                  <div class="text-end">
                    <div v-html="sanitize(getCheckoutDiscountText)"></div>
                    <div v-html="sanitize(discountDurationValidForText)"></div>
                  </div>
                </div>
                <hr v-if="canShowCouponCode" />
                <div
                  v-if="getSelectedPriceOption.tax"
                  class="tax-info d-flex flex-row justify-content-between align-items-start mt-2"
                >
                  <span>{{ product.tax_rate_name }}</span>
                  <span class="c-charcoal">
                    {{ getTaxText }}
                  </span>
                </div>
                <hr v-if="getSelectedPriceOption.tax" />
                <div
                  class="checkout-summary d-flex flex-row justify-content-between align-items-start mt-2"
                >
                  <span>{{ $t('PRODUCTS.TITLE.SUMMARY') }}:</span>
                  <div class="d-flex flex-column text-end">
                    <div v-if="hasTrial">
                      {{ getCheckoutTrialText }}
                    </div>
                    <div
                      class="h2 c-charcoal m-0"
                      v-html="sanitize(getPriceTranslation)"
                    ></div>
                    <div
                      v-if="getCheckoutNonDiscountedPriceThereafterText"
                      class="small mt-2"
                      v-html="
                        sanitize(getCheckoutNonDiscountedPriceThereafterText)
                      "
                    ></div>
                    <div
                      v-if="getSelectedPriceOption.isPaymentPlan"
                      class="small mt-2"
                      v-html="sanitize(getPaymentPlanThereafterText)"
                    ></div>
                    <div class="small mt-2">
                      {{
                        $t('STUDIO.TEXT.ALL_PRICES_ARE_IN_CURRENCY', {
                          currency: studio.currency || 'USD',
                        })
                      }}
                    </div>
                    <div v-if="hasTrial" class="small c-charcoal mt-3">
                      <i>{{
                        $t('PRODUCTS.LABELS.FREE_TRIAL_CANCELLATION_POLICY')
                      }}</i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bottom-section footer-info w-100">
              <div
                class="d-flex flex-row justify-content-between align-items-center flex-wrap"
              >
                <h3 class="me-2">{{ $t('PRODUCTS.LABELS.NEED_ANY_HELP') }}</h3>
                <a
                  class="undecorated-router-link d-block"
                  :href="`mailto:${studio.email}`"
                >
                  <span>
                    <i class="fal fa-comments-alt c-brand-color"></i>
                    {{ $t('STUDIO.LABELS.CONTACT_US') }}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { errorToMessage } from '@/utils/errors';
import { mapState } from 'pinia';
import StudentLogin from '@/components/signup/StudentLogin.vue';
import Signup from '@/components/signup/Signup.vue';
import ProductPostPurchaseModal from '@/components/products/ProductPostPurchaseModal.vue';
import { getProductTypeTranslations } from '@/utils/products';
import {
  trackFBPixelStudioPurchase,
  trackFBPixelStudioFreePurchase,
} from '@/services/analytics';
import ProductThumbnail from '@/components/products/ProductThumbnail.vue';
import { loadDeadlineFunnelScript } from '@/utils/deadlinefunnel';
import { removeTrailingSlash } from '@/utils/utils';
import { sanitize } from '@/utils/utils';
import StripePaymentModal from '@/components/products/StripePaymentModal.vue';
import Cookies from 'js-cookie';
import StudentCheckoutInformationForm from './StudentCheckoutInformationForm.vue';
import _ from 'lodash-es';
import { metaTitles } from '@/utils/meta-utils';
import { renderHtmlFromEditorJS } from '@/utils/utils';
import HMDialog from '@/components/common/HMDialog.vue';
import { useStudioStore, useUserStore, useProductStore } from '@/stores';
import { Form } from 'vee-validate';

const RECURRING_OPTIONS = {
  annually: {
    order: 0,
    translation: 'PRODUCTS.LABELS.ANNUAL_SUBSCRIPTION',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_PER_YEAR',
    label: 'year',
    payPalPeriod: 'Y',
    billingInterval: 1,
  },
  biannually: {
    order: 1,
    translation: 'PRODUCTS.LABELS.BIANNUAL_SUBSCRIPTION',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_PER_BIANNUAL',
    label: 'quarter',
    payPalPeriod: 'M',
    billingInterval: 6,
  },
  quarterly: {
    order: 2,
    translation: 'PRODUCTS.LABELS.QUARTERLY_SUBSCRIPTION',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_PER_QUARTER',
    label: 'quarter',
    payPalPeriod: 'M',
    billingInterval: 3,
  },
  monthly: {
    order: 3,
    translation: 'PRODUCTS.LABELS.MONTHLY_SUBSCRIPTION',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_PER_MONTH',
    label: 'month',
    payPalPeriod: 'M',
    billingInterval: 1,
  },
  weekly: {
    order: 4,
    translation: 'PRODUCTS.LABELS.WEEKLY_SUBSCRIPTION',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_PER_WEEK',
    label: 'week',
    payPalPeriod: 'W',
    billingInterval: 1,
  },
  daily: {
    order: 5,
    translation: 'PRODUCTS.LABELS.DAILY_SUBSCRIPTION',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_PER_DAY',
    label: 'day',
    payPalPeriod: 'D',
    billingInterval: 1,
  },
};

export default {
  name: 'StudioCheckout',
  components: {
    ProductThumbnail,
    StudentCheckoutInformationForm,
    Form
  },
  metaInfo() {
    return {
      title: this.metaTitle,
      meta: [
        ...metaTitles(this.metaTitle),
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.headerImage,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: getCurrentUrl(),
        },
      ],
    };
  },
  props: {
    viewButtonText: {
      type: String,
      default: 'PRODUCTS.BUTTON.VIEW_PRODUCT',
    },
    viewRouteName: {
      type: String,
      default: 'studio-product',
    },
    eventId: {
      type: Number,
      default: null,
    },
  },
  computed: {
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
      currency: 'currency',
    }),
    ...mapState(useUserStore, {
      isLoggedIn: 'isLoggedIn',
      user: 'user',
    }),
    metaTitle() {
      return this.product?.product_name
        ? `${this.product?.product_name} — ${this.studio?.studio_name}`
        : null;
    },
    checkoutSubtitle() {
      // Optional marketing line shown between the checkout title and the price
      // options. Set per product by the teacher on the product's Pricing tab;
      // empty for products that don't use it. Teachers can style
      // `.checkout-subtitle` through their custom code injection.
      return this.product?.checkout_subtitle || '';
    },
    headerImage() {
      return this.product?.cover_file?.image_url;
    },
    hasTermsAndConditions() {
      return (
        this.product?.terms_and_conditions?.length &&
        renderHtmlFromEditorJS(this.product?.terms_and_conditions).length > 0
      );
    },
    completedTermsAndConditions() {
      if (!this.hasTermsAndConditions) return true;
      return this.agreedToTerms;
    },
    optedInMarketingEmails() {
      return this.studio?.is_opted_in;
    },
    isSmtpConnected() {
      return this.studio?.is_smtp_connected;
    },
    isCheckoutAllowed() {
      return this.product?.enrollment_available;
    },
    productId() {
      return this.$route.params.product_id;
    },
    studioURL() {
      return this.$studioURL;
    },
    isRecurring() {
      return this.product.pricing_type === 'recurring';
    },
    isRecurringMultiple() {
      return this.product.recurring_type === 'multiple';
    },
    isFixedPrice() {
      return this.product.pricing_type === 'fixed';
    },
    isFreePrice() {
      return this.product.pricing_type === 'free';
    },
    isClassCard() {
      return this.product.product_type === 'punch_card';
    },
    isPayPalProcessor() {
      if (!this.productForPurchase) return false;
      return this.productForPurchase.payment_gateway === 'paypal';
    },
    isStripeProcessor() {
      if (!this.productForPurchase) return false;
      return this.productForPurchase.payment_gateway === 'stripe';
    },
    getPaymentPlanActivationCode() {
      return this.$route.query.pp;
    },
    getProductType() {
      return getProductTypeTranslations(this.product.product_type);
    },
    getPayPalCheckoutUrl() {
      return `https://www${
        process.env.VUE_APP_PAYPAL_SANDBOX === 'true' ? '.sandbox' : ''
      }.paypal.com/us/cgi-bin/webscr`;
    },
    getPayPalNotifyUrl() {
      return `${removeTrailingSlash(
        process.env.VUE_APP_API_BASE_URL
      )}/paypal-callback`;
    },
    getPayPalReturnUrl() {
      return `${removeTrailingSlash(
        process.env.VUE_APP_API_BASE_URL
      )}/paypal-post-purchase/${this.productId}`;
    },
    getPayPalCustomParametersField() {
      if (!this.user) return '';
      const selectedOption = this.getSelectedPriceOption;
      if (!selectedOption) return '';
      let daypasParam = '';
      if (selectedOption.isDaypass) {
        daypasParam = '&daypass=true';
      }
      return `?product_id=${this.productId}&student_id=${
        this.user.student.id
      }&coupon_code=${this.coupon ? this.coupon.code : ''}&recurring_option=${
        selectedOption.isRecurring ? selectedOption.value : ''
      }${daypasParam}`;
    },
    getPurchaseButtonText() {
      const selectedOption = this.getSelectedPriceOption;
      if (!selectedOption) return '';
      if (selectedOption.isDaypass) {
        return selectedOption.price <= 0
          ? this.$t('PRODUCTS.BUTTON.REGISTER_FREE_DAY_PASS')
          : this.$t('PRODUCTS.BUTTON.PURCHASE_PRODUCT_DAY_PASS');
      } else {
        if (selectedOption.isRecurring) {
          if (this.isForeverFree) {
            return this.$t('PRODUCTS.BUTTON.REGISTER_FREE_PRODUCT');
          } else {
            if (this.hasTrial)
              return this.$t('PRODUCTS.BUTTON.START_FREE_TRIAL');

            return this.$t('PRODUCTS.BUTTON.PURCHASE_SUBSCRIPTION');
          }
        }
        return selectedOption.price <= 0
          ? this.$t('PRODUCTS.BUTTON.REGISTER_FREE_PRODUCT')
          : this.$t('PRODUCTS.BUTTON.PURCHASE_PRODUCT');
      }
    },
    checkoutButtonDisabledText() {
      if (!this.isLoggedIn) {
        return this.$t(
          'PRODUCTS.TEXT.YOU_NEED_TO_LOGIN_OR_CREATE_AN_ACCOUNT_FIRST'
        );
      }
      if (!this.studentDataValid) {
        return this.$t(
          'PRODUCTS.TEXT.BUTTON_WILL_BE_ENABLED_WHEN_MANDATORY_INFORMATION_IS_ENTERED'
        );
      }
      if (!this.completedTermsAndConditions) {
        return this.$t(
          'PRODUCTS.TEXT.YOU_NEED_TO_AGREE_TO_TERMS_AND_CONDITIONS'
        );
      }
      if (this.inProgress) {
        return '';
      }
      if (this.disabledCheckout) {
        return this.$t(
          'PRODUCTS.TEXT.BUTTON_WILL_BE_ENABLED_WHEN_CREDIT_CARD_INFORMATION_IS_ENTERED'
        );
      }
      return '';
    },
    trialPeriodUnit() {
      switch (this.product.trial_period) {
        case 'Y':
          return this.$t('PRODUCTS.LABELS.YEAR');
        case 'M':
          return this.$t('PRODUCTS.LABELS.MONTH');
        case 'W':
          return this.$t('PRODUCTS.LABELS.WEEK');
        case 'D':
          return this.$t('PRODUCTS.LABELS.DAY');
      }
      return '';
    },
    getFreeTrialHeading() {
      const count = {
        count: this.product.trial_units,
      };
      switch (this.product.trial_period) {
        case 'Y':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_YEAR_VARIANT',
            count,
            this.product.trial_units,
          );
        case 'M':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_MONTH_VARIANT',
            count,
            this.product.trial_units,
          );
        case 'W':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_WEEK_VARIANT',
            count,
            this.product.trial_units,
          );
        case 'D':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_DAY_VARIANT',
            count,
            this.product.trial_units,
          );
      }
      return '';
    },
    getFreeTrialText() {
      const count = {
        count: this.product.trial_units,
      };
      switch (this.product.trial_period) {
        case 'Y':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_FOR_THEN_YEAR_VARIANT',
            count,
            this.product.trial_units,
          );
        case 'M':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_FOR_THEN_MONTH_VARIANT',
            this.product.trial_units
          );
        case 'W':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_FOR_THEN_WEEK_VARIANT',
            this.product.trial_units
          );
        case 'D':
          return this.$t(
            'PRODUCTS.LABELS.FREE_TRIAL_FOR_THEN_DAY_VARIANT',
            this.product.trial_units
          );
      }
      return '';
    },
    discountDurationValidForText() {
      if (!this.coupon) return '';
      const option = this.getSelectedPriceOption;
      if (!option.isRecurring) return '';
      switch (this.coupon.duration_type) {
        case 'unlimited':
          return '';
        case 'months':
          return `${this.$t('PRODUCTS.LABELS.VALID_FOR')} ${
            this.coupon.duration_units
          } ${this.$t(
            'PRODUCTS.LABELS.MONTH_VARIANT',
            this.coupon.duration_units
          )}`;
        case 'weeks':
          return `${this.$t('PRODUCTS.LABELS.VALID_FOR')} ${
            this.coupon.duration_units
          } ${this.$t(
            'PRODUCTS.LABELS.WEEK_VARIANT',
            this.coupon.duration_units
          )}`;
        case 'days':
          return `${this.$t('PRODUCTS.LABELS.VALID_FOR')} ${
            this.coupon.duration_units
          } ${this.$t(
            'PRODUCTS.LABELS.DAY_VARIANT',
            this.coupon.duration_units
          )}`;
      }
      return '';
    },
    discountDurationAfterText() {
      if (!this.coupon) return '';
      switch (this.coupon.duration_type) {
        case 'unlimited':
          return '';
        case 'months':
          return `${this.$t('PRODUCTS.LABELS.AFTER')} ${
            this.coupon.duration_units
          } ${this.$t(
            'PRODUCTS.LABELS.MONTH_VARIANT',
            this.coupon.duration_units
          )}`;
        case 'weeks':
          return `${this.$t('PRODUCTS.LABELS.AFTER')} ${
            this.coupon.duration_units
          } ${this.$t(
            'PRODUCTS.LABELS.WEEK_VARIANT',
            this.coupon.duration_units
          )}`;
        case 'days':
          return `${this.$t('PRODUCTS.LABELS.AFTER')} ${
            this.coupon.duration_units
          } ${this.$t(
            'PRODUCTS.LABELS.DAY_VARIANT',
            this.coupon.duration_units
          )}`;
      }
      return '';
    },
    hasTrial() {
      if (!this.getSelectedPriceOption || !this.productForPurchase)
        return false;
      return (
        this.getSelectedPriceOption.isRecurring &&
        this.productForPurchase.trial &&
        this.productForPurchase.trial_units &&
        this.productForPurchase.trial_period
      );
    },
    getCheckoutTrialText() {
      if (!this.hasTrial) return '';
      return this.getFreeTrialText;
    },
    getCheckoutOriginalPriceWithTaxText() {
      return this.getSelectedPriceOption.originalPriceWithTaxTranslation;
    },
    getCheckoutDiscountText() {
      const price = this.getSelectedPriceOption.originalPrice;
      const couponDiscount = this.getCouponDiscount(price);
      return `
          <span class="c-charcoal">
            - ${this.$formatCurrency(couponDiscount, this.currency)}
          </span>
          <span>
            ${this.$t('PRODUCTS.LABELS.COUPON_DISCOUNT')}
          </span>
        `;
    },
    getCheckoutNonDiscountedPriceThereafterText() {
      if (!this.coupon) return '';
      if (this.coupon.duration_type === 'unlimited') return '';
      const option = this.getSelectedPriceOption;
      if (!option.isRecurring) return '';
      return `<span class="c-charcoal">${this.getCheckoutOriginalPriceWithTaxText}</span> ${this.discountDurationAfterText}`;
    },
    getPaymentPlanThereafterText() {
      if (this.wishPriceSelected) return '';
      const option = this.getSelectedPriceOption;
      return option.summary;
    },
    isForeverFree() {
      if (this.isFreePrice) return true;
      if (!this.getSelectedPriceOption) return false;
      if (this.getSelectedPriceOption.price > 0) return false;

      // calculated price for now is 0, check if price 0 is for unlimited duration
      if (this.coupon && this.coupon.duration_type === 'unlimited') return true;
      if (this.coupon && this.getSelectedPriceOption.isDaypass) return true;
      if (this.coupon && !this.getSelectedPriceOption.isRecurring) return true;

      return false;
    },
    priceOptions() {
      const options = [];
      if (this.isRecurring) {
        if (this.isRecurringMultiple) {
          const recurringOptions = Object.keys(
            this.product.recurring_options
          ).sort((o1, o2) => {
            return (
              RECURRING_OPTIONS[o1.toLowerCase()].order -
              RECURRING_OPTIONS[o2.toLowerCase()].order
            );
          });
          recurringOptions.forEach(option => {
            option = option.toLowerCase();
            if (this.product.recurring_options[option].available === true) {
              const originalPrice = parseFloat(
                this.product.recurring_options[option].price || 0
              );
              const discountedPrice = this.getCouponDiscountPrice(
                originalPrice
              );
              const originalPriceTax = this.getTax(originalPrice);
              const tax = this.getTax(discountedPrice);
              const taxWithCurrency = this.$formatCurrency(
                tax || 0,
                this.currency
              );
              const finalPrice = discountedPrice + tax;
              const price = this.$formatCurrency(
                finalPrice || 0,
                this.currency
              );
              const originalPriceWithCurrency = this.$formatCurrency(
                originalPrice || 0,
                this.currency
              );
              const originalPriceWithTax = originalPrice + originalPriceTax;
              const originalPriceWithTaxWithCurrency = this.$formatCurrency(
                originalPriceWithTax || 0,
                this.currency
              );
              const wishPriceTax = this.getTax(
                this.product.recurring_options[option].min_wish_price
              );
              const wishPriceAmount =
                parseFloat(
                  this.product.recurring_options[option].min_wish_price
                ) + wishPriceTax;
              const wishPrice = this.$formatCurrency(
                wishPriceAmount,
                this.currency
              );
              const recurringOption = RECURRING_OPTIONS[option];
              options.push({
                html: sanitize(
                  this.$t(recurringOption.translation, {
                    price: originalPriceWithCurrency,
                  })
                ),
                value: option,
                originalPriceTranslation: this.$t(
                  recurringOption.priceTranslation,
                  {
                    price: originalPriceWithCurrency,
                  }
                ),
                originalPriceWithTaxTranslation: this.$t(
                  recurringOption.priceTranslation,
                  {
                    price: originalPriceWithTaxWithCurrency,
                  }
                ),
                priceTranslation: this.$t(recurringOption.priceTranslation, {
                  price,
                }),
                priceTranslationKey: recurringOption.priceTranslation,
                originalPrice: originalPrice,
                price: finalPrice,
                wishPrice: wishPrice,
                wishPriceAmount: wishPriceAmount,
                netPrice: discountedPrice,
                tax: tax,
                taxWithCurrency: taxWithCurrency,
                isRecurring: true,
                isMultipleRecurring: true,
                payPalPeriod: recurringOption.payPalPeriod,
                billingInterval: recurringOption.billingInterval,
                isDiscountable: true,
              });
            }
          });
        } else {
          const originalPrice = parseFloat(this.product.price || 0);
          const discountedPrice = this.getCouponDiscountPrice(originalPrice);

          const originalPriceTax = this.getTax(originalPrice);
          const tax = this.getTax(discountedPrice);
          const taxWithCurrency = this.$formatCurrency(tax || 0, this.currency);
          const finalPrice = discountedPrice + tax;
          const price = this.$formatCurrency(finalPrice, this.currency);
          const originalPriceWithCurrency = this.$formatCurrency(
            originalPrice || 0,
            this.currency
          );

          const originalPriceWithTax = originalPrice + originalPriceTax;
          const originalPriceWithTaxWithCurrency = this.$formatCurrency(
            originalPriceWithTax || 0,
            this.currency
          );

          const wishPriceTax = this.getTax(this.product.min_wish_price);
          const wishPriceAmount =
            parseFloat(this.product.min_wish_price) + wishPriceTax;
          const wishPrice = this.$formatCurrency(
            wishPriceAmount,
            this.currency
          );

          const recurringOption =
            RECURRING_OPTIONS[this.product.recurring_type];
          options.push({
            html: sanitize(
              this.$t(recurringOption.translation, {
                price: originalPriceWithCurrency,
              })
            ),
            value: this.product.recurring_type,
            originalPriceTranslation: this.$t(
              recurringOption.priceTranslation,
              {
                price: originalPriceWithCurrency,
              }
            ),
            originalPriceWithTaxTranslation: this.$t(
              recurringOption.priceTranslation,
              {
                price: originalPriceWithTaxWithCurrency,
              }
            ),
            priceTranslation: this.$t(recurringOption.priceTranslation, {
              price,
            }),
            priceTranslationKey: recurringOption.priceTranslation,
            originalPrice: originalPrice,
            price: finalPrice,
            wishPrice: wishPrice,
            wishPriceAmount: wishPriceAmount,
            netPrice: discountedPrice,
            tax: tax,
            taxWithCurrency: taxWithCurrency,
            isRecurring: true,
            payPalPeriod: recurringOption.payPalPeriod,
            billingInterval: recurringOption.billingInterval,
            isDiscountable: true,
          });
        }
      }
      if (this.isFixedPrice) {
        const originalPrice = parseFloat(this.product.price || 0);
        const discountedPrice = this.getCouponDiscountPrice(originalPrice);
        const originalPriceTax = this.getTax(originalPrice);
        const tax = this.getTax(discountedPrice);
        const taxWithCurrency = this.$formatCurrency(tax || 0, this.currency);
        const finalPrice = discountedPrice + tax;
        const price = this.$formatCurrency(finalPrice, this.currency);
        const originalPriceWithCurrency = this.$formatCurrency(
          originalPrice || 0,
          this.currency
        );
        const originalPriceWithTax = originalPrice + originalPriceTax;
        const originalPriceWithTaxWithCurrency = this.$formatCurrency(
          originalPriceWithTax || 0,
          this.currency
        );

        const wishPriceTax = this.getTax(this.product.min_wish_price);
        const wishPriceAmount =
          parseFloat(this.product.min_wish_price) + wishPriceTax;
        const wishPrice = this.$formatCurrency(wishPriceAmount, this.currency);

        options.push({
          html: sanitize(
            this.$t('PRODUCTS.LABELS.ONE_TIME', {
              price: originalPriceWithCurrency,
            })
          ),
          value: 'fixed',
          originalPriceTranslation: `${originalPriceWithCurrency}`,
          originalPriceWithTaxTranslation: `${originalPriceWithTaxWithCurrency}`,
          priceTranslation: `${price}`,
          originalPrice: originalPrice,
          price: finalPrice,
          wishPrice: wishPrice,
          wishPriceAmount: wishPriceAmount,
          netPrice: discountedPrice,
          tax: tax,
          taxWithCurrency: taxWithCurrency,
          isRecurring: false,
          isDiscountable: true,
        });

        if (this.isStripeProcessor) {
          const paymentPlans = this.getPaymentPlanActivationCode
            ? this.product.payment_plans.filter(
                pp => pp.activation_code === this.getPaymentPlanActivationCode
              )
            : this.product.payment_plans.filter(pp => !pp.activation_code);
          paymentPlans.forEach(paymentPlan => {
            const currency = paymentPlan.currency;
            const initialPaymentAmount = paymentPlan.initial_payment_amount;
            const numberOfSubsequentPayments =
              paymentPlan.number_of_subsequent_payments;
            const subsequentPaymentAmount =
              paymentPlan.subsequent_payment_amount;
            const billingFrequencyDays = paymentPlan.billing_frequency_days;

            const originalPrice = parseFloat(initialPaymentAmount || 0);
            const originalSubsequentPrice = parseFloat(
              subsequentPaymentAmount || 0
            );
            const discountedPrice = originalPrice;

            // calc taxes
            const originalPriceTax = this.getTax(originalPrice);
            const tax = this.getTax(initialPaymentAmount);
            const taxWithCurrency = this.$formatCurrency(
              tax || 0,
              this.currency
            );
            const subsequentPaymentTax = this.getTax(subsequentPaymentAmount);
            const subsequentPaymentTaxWithCurrency = this.$formatCurrency(
              subsequentPaymentTax || 0,
              this.currency
            );
            const finalPrice = originalPrice + tax;
            const finalSubsequentPrice =
              originalSubsequentPrice + subsequentPaymentTax;
            const price = this.$formatCurrency(
              finalPrice || 0,
              paymentPlan.currency
            );
            const originalPriceWithCurrency = this.$formatCurrency(
              originalPrice || 0,
              paymentPlan.currency
            );
            const initialPaymentAmountWithCurrency = this.$formatCurrency(
              originalPrice || 0,
              paymentPlan.currency
            );
            const subsequentPaymentAmountCurrency = this.$formatCurrency(
              originalSubsequentPrice || 0,
              paymentPlan.currency
            );
            const subsequentPaymentAmountCurrencyWithTax = this.$formatCurrency(
              finalSubsequentPrice || 0,
              paymentPlan.currency
            );

            const text = `${this.$t(
              'PRODUCTS.TEXT.PAYMENT_PLAN_DESCRIPTION_VARIANT',
              {
                currency,
                initialPaymentAmount: initialPaymentAmountWithCurrency,
                numberOfSubsequentPayments,
                subsequentPaymentAmount: subsequentPaymentAmountCurrency,
                billingFrequencyDays,
              },
              numberOfSubsequentPayments,
            )}`;
            const summaryText = `${this.$t(
              'PRODUCTS.TEXT.PAYMENT_PLAN_SUMMARY_VARIANT',
              {
                numberOfSubsequentPayments,
                subsequentPaymentAmount: subsequentPaymentAmountCurrencyWithTax,
                billingFrequencyDays,
              },
              numberOfSubsequentPayments,
            )}`;
            options.push({
              html: sanitize(
                this.$t('PRODUCTS.LABELS.PAYMENT_PLAN_CHECKOUT', {
                  text,
                })
              ),
              summary: summaryText,
              selectedTitle: this.$t('PRODUCTS.TEXT.PAYMENT_PLAN'),
              value: `payment_plan_${paymentPlan.id}`,
              originalPriceTranslation: `${originalPriceWithCurrency}`,
              priceTranslation: `${price}`,
              originalPrice: originalPrice,
              price: finalPrice,
              netPrice: discountedPrice,
              tax: tax,
              taxWithCurrency: taxWithCurrency,
              subsequentPaymentTax: subsequentPaymentTax,
              subsequentPaymentTaxWithCurrency: subsequentPaymentTaxWithCurrency,
              isRecurring: false,
              isPaymentPlan: true,
              paymentPlan: paymentPlan,
              isDiscountable: false,
              disabledWishPrice: true,
            });
          });
        }
      }
      if (this.product.day_pass_enabled) {
        const originalPrice = parseFloat(this.product.day_pass_price || 0);
        const discountedPrice = this.getCouponDiscountPrice(originalPrice);
        const originalPriceTax = this.getTax(originalPrice);
        const tax = this.getTax(discountedPrice);
        const taxWithCurrency = this.$formatCurrency(tax || 0, this.currency);
        const finalPrice = discountedPrice + tax;
        const price = this.$formatCurrency(finalPrice, this.currency);
        const originalPriceWithCurrency = this.$formatCurrency(
          originalPrice || 0,
          this.currency
        );
        const wishPriceTax = this.getTax(this.product.min_wish_price);
        const wishPriceAmount =
          parseFloat(this.product.min_wish_price) + wishPriceTax;
        const wishPrice = this.$formatCurrency(wishPriceAmount, this.currency);
        options.push({
          html: sanitize(
            this.$t('PRODUCTS.LABELS.SINGLE_DAY_ACCESS_PASS', {
              price: originalPriceWithCurrency,
            })
          ),
          value: 'daypass',
          originalPriceTranslation: `${originalPriceWithCurrency}`,
          priceTranslation: `${price}`,
          originalPrice: originalPrice,
          price: finalPrice,
          wishPrice: wishPrice,
          wishPriceAmount: wishPriceAmount,
          netPrice: discountedPrice,
          tax: tax,
          taxWithCurrency: taxWithCurrency,
          isRecurring: false,
          isDaypass: true,
          isDiscountable: true,
          disabledWishPrice: true,
        });
      }
      if (this.isFreePrice) {
        const originalPrice = 0;
        const rawPrice = 0;
        const price = this.$formatCurrency(rawPrice, this.currency);
        const originalPriceWithCurrency = this.$formatCurrency(
          originalPrice,
          this.currency
        );
        options.push({
          html: sanitize(this.$t('PRODUCTS.LABELS.FREE_PRODUCT')),
          value: 'free',
          originalPriceTranslation: this.$t('PRODUCTS.LABELS.FREE'),
          priceTranslation: this.$t('PRODUCTS.LABELS.FREE'),
          originalPrice: originalPrice,
          price: rawPrice,
          isRecurring: false,
          isDaypass: false,
          isFree: true,
          isDiscountable: false,
          disabledWishPrice: true,
        });
      }
      return options;
    },
    getSelectedPriceOption() {
      const priceOption = this.priceOptions.find(
        option => option.value === this.selectedPriceOption
      );
      return priceOption || {};
    },
    getSelectedPriceOptionWishPriceTranslation() {
      const priceOption = this.getSelectedPriceOption;
      const selectedPriceOption = priceOption ? { ...priceOption } : {};

      if (this.wishPriceSelected) {
        const price = this.$formatCurrency(
          this.wishPriceSelected,
          this.currency
        );
        if (selectedPriceOption.priceTranslationKey) {
          return sanitize(
            this.$t(selectedPriceOption.priceTranslationKey, {
              price: price,
            })
          );
        } else {
          return price;
        }
      }

      return null;
    },
    getSelectedPriceOptionWishPriceTax() {
      if (this.wishPriceSelected) {
        const tax = this.getTax(this.getPreTaxAmount(this.wishPriceSelected));
        return this.$formatCurrency(tax, this.currency);
      }

      return null;
    },
    getTaxText() {
      return (
        this.getSelectedPriceOptionWishPriceTax ||
        this.getSelectedPriceOption.taxWithCurrency
      );
    },
    getPriceTranslation() {
      return (
        this.getSelectedPriceOptionWishPriceTranslation ||
        this.getSelectedPriceOption.priceTranslation
      );
    },
    getPrice() {
      return this.wishPriceSelected || this.getSelectedPriceOption.price;
    },
    canApplyWishPrice() {
      return (
        this.wishPrice >=
          parseFloat(this.getSelectedPriceOption.wishPriceAmount) ||
        !this.wishPrice
      );
    },
    isFreeCheckout() {
      return this.getSelectedPriceOption?.price <= 0;
    },
    disabledCheckout() {
      if (!this.isLoggedIn) return true;
      if (this.inProgress) return true;
      if (!this.completedTermsAndConditions) return true;
      if (this.isFreeCheckout) return false;

      // make sure we have the custom params available when handling paypal payments
      if (this.isPayPalProcessor && !this.getPayPalCustomParametersField)
        return true;

      // if (this.isStripeProcessor) {
      //   if (this.useExistingCustomerData) return false;
      //   if (!this.stripeCompleted) return true;
      // }

      return false;
    },
    fbPixelPurchasePayload() {
      const selectedOption = this.getSelectedPriceOption;
      if (!selectedOption) return {};
      let type = '';
      if (selectedOption.isDaypass) {
        type = 'daypass';
      } else {
        type = selectedOption.isRecurring ? 'recurring' : 'fixed';
      }

      const payload = {
        value: selectedOption.price,
        currency: this.currency,
        type: type,
      };
      if (selectedOption.isRecurring) {
        payload.billing_interval = selectedOption.value;
      }
      if (this.hasTrial) {
        payload.trial_info = `${this.product.trial_units} ${this.trialPeriodUnit}`;
      }
      return payload;
    },
    canShowCouponCode() {
      const selectedPriceOption =
        this.priceOptions.find(
          option => option.value === this.selectedPriceOption
        ) || {};
      return selectedPriceOption.isDiscountable && !this.wishPriceSelected;
    },
    stripeCustomerData() {
      return this.productForPurchase?.existing_stripe_customer;
    },
    existingCreditCardPaymentId() {
      return this.stripeCustomerData?.invoice_settings?.default_payment_method
        ?.id;
    },
    existingCreditCard() {
      return this.stripeCustomerData?.invoice_settings?.default_payment_method
        ?.card;
    },
    existingCreditCardBrand() {
      return this.capitalize(this.existingCreditCard?.brand);
    },
    existingCreditCardText() {
      if (!this.existingCreditCard) {
        return '';
      }
      const card = this.existingCreditCard;
      return `<b>${this.existingCreditCardBrand}</b> ending with <b>${card.last4}</b> and <b>${card.exp_month}/${card.exp_year}</b> expiration`;
    },
    checkoutParameters() {
      const obj = {
        selectedPriceOption: encodeURIComponent(this.selectedPriceOption ?? ''),
        wishPrice: encodeURIComponent(this.wishPrice ?? ''),
        wishPriceSelected: encodeURIComponent(this.wishPriceSelected ?? ''),
        couponCode: encodeURIComponent(this.couponCode ?? ''),
      };
      return `?${new URLSearchParams(obj).toString()}`;
    },
  },
  data() {
    return {
      product: null,
      productForPurchase: null,
      selectedPriceOption: '',
      coupon: null,
      couponCode: null,
      couponError: null,
      isCheckingCoupon: false,
      stripeError: false,
      stripeCompleted: false,
      inProgress: false,
      loading: false,
      wishPrice: undefined,
      wishPriceSelected: null,
      useExistingCustomerData: false,
      setupIntent: null,
      elements: null,
      optedIn: false,
      agreedToTerms: false,
      studentData: {},
      studentDataValid: true,
    };
  },
  methods: {
    sanitize,
    openTermsAndConditionsModal(event) {
      event.stopPropagation();
      event.preventDefault();
      this.$modal.show(HMDialog, {
        title: this.$t('PRODUCTS.TITLE.TERMS_AND_CONDITIONS'),
        htmlContent: renderHtmlFromEditorJS(this.product?.terms_and_conditions),
        confirmButton: null,
        confirm: () => {},
      });
    },
    async optIntoMarketing(optIn) {
      await useStudioStore().optInToStudioMarketing({
        studioURL: this.studioURL,
        optIn,
      });
    },
    priceOptionChanged() {
      console.log('priceOptionChanged', this.selectedPriceOption);
    },
    capitalize(s) {
      if (!s) return s;
      return s[0].toUpperCase() + s.slice(1);
    },
    getPreTaxAmount(amount) {
      return amount / (1 + this.product.tax_rate_percent / 100);
    },
    getTax(price) {
      if (!this.product.tax_rate_percent) return 0;
      const tax =
        Math.round(
          parseFloat(price) *
            (parseFloat(this.product.tax_rate_percent) / 100.0) *
            100
        ) / 100;

      return tax;
    },
    getPaymentPlanOption(activationCode) {
      return (
        this.priceOptions.find(
          option =>
            option.isPaymentPlan &&
            option.paymentPlan.activation_code === activationCode
        ) || {}
      );
    },
    async purchaseFreeProduct(reCaptchaToken) {
      if (!this.isFreePrice) return;
      this.inProgress = true;
      try {
        await this.updateStudentInfo();

        await useProductStore().purchaseFreeProduct({
          studioURL: this.studioURL,
          productId: this.productId,
          reCaptchaToken: reCaptchaToken,
        });

        useStudioStore().getStudio(this.studioURL);

        trackFBPixelStudioFreePurchase(this.studio.fb_pixel_id, {
          value: 0,
          currency: this.currency,
          type: 'free',
        });

        this.$modal.show(
          ProductPostPurchaseModal,
          {
            hideHeader: true,
            product: this.product,
            studioURL: this.studioURL,
            viewButtonText: this.viewButtonText,
            viewRouteName: this.viewRouteName,
            eventId: this.eventId,
            onClose: () => {
              this.inProgress = false;
            },
          },
          {
            clickToClose: false,
            escToClose: false,
          }
        );
      } catch (error) {
        this.inProgress = false;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Product registration failed',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async purchase() {
      if (this.inProgress) return;

      this.inProgress = true;

      const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
      const reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
        action: 'buyproduct',
      });

      const selectedPriceOption = this.getSelectedPriceOption;
      const isFreeDayPass =
        this.selectedPriceOption.isDaypass &&
        this.selectedPriceOption.price <= 0;
      if (this.isFreePrice || isFreeDayPass) {
        this.purchaseFreeProduct(reCaptchaToken);
        return;
      }

      const coupon = this.coupon?.code;
      const studioURL = this.studioURL;
      const productId = this.productId;
      const recurringOption = selectedPriceOption.isMultipleRecurring
        ? selectedPriceOption.value
        : null;
      const daypass = selectedPriceOption.isDaypass;
      const isPaymentPlan = selectedPriceOption.isPaymentPlan;
      const paymentPlanId = selectedPriceOption.paymentPlan?.id;
      const customAmount = this.wishPriceSelected;
      const useSavedCard = this.useExistingCustomerData;
      const rewardfulReferralId = window.Rewardful?.referral;

      const purchasePayload = {
        coupon,
        studioURL,
        productId,
        recurringOption,
        daypass,
        isPaymentPlan,
        paymentPlanId,
        customAmount,
        useSavedCard,
        reCaptchaToken,
        rewardfulReferralId,
      };

      try {
        await this.updateStudentInfo();
        const purchaseResult = await useProductStore().purchaseProduct(purchasePayload);

        if (purchaseResult.purchase) {
          // only for free products, other will be processed async
          useStudioStore().getStudio(this.studioURL);

          trackFBPixelStudioPurchase(
            this.studio.fb_pixel_id,
            this.fbPixelPurchasePayload
          );

          this.$router.push({
            name: 'studio-product-checkout-stripe-confirm',
            params: { product_id: productId },
          });

          // this.$modal.show(
          //   ProductPostPurchaseModal,
          //   {
          //     hideHeader: true,
          //     product: this.product,
          //     studioURL: this.studioURL,
          //     viewButtonText: this.viewButtonText,
          //     viewRouteName: this.viewRouteName,
          //     eventId: this.eventId,
          //     onClose: () => {
          //       this.inProgress = false;
          //     },
          //   },
          //   {
          //     clickToClose: false,
          //     escToClose: false,
          //   }
          // );
        } else {
          const fbPixelCookie = {
            fb_pixel_id: this.studio.fb_pixel_id,
            payload: this.fbPixelPurchasePayload,
          };
          Cookies.set('fbPurchasePayload', JSON.stringify(fbPixelCookie));
          this.$modal.show(
            StripePaymentModal,
            {
              user: this.user,
              product: this.product,
              setupIntent: purchaseResult.setup_intent,
              paymentIntent: purchaseResult.payment_intent,
              studioURL: this.studioURL,
              stripePublishableKey: this.productForPurchase
                .stripe_publishable_key,
              buttonText: this.getPurchaseButtonText,
              onClose: () => {
                this.inProgress = false;
              },
            },
            {
              clickToClose: false,
              escToClose: false,
            }
          );
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Purchase failed',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
    getCouponDiscountPrice(price) {
      if (!this.coupon) return price;
      let discountedPrice = price;
      if (this.coupon.discount_type === 'fixed_amount') {
        const new_price = price - this.coupon.discount_amount;
        discountedPrice = new_price < 0 ? 0 : new_price;
      } else if (this.coupon.discount_type === 'percentage') {
        discountedPrice = price - price * (this.coupon.discount_amount / 100);
      }
      return Math.max(discountedPrice, 0);
    },
    getCouponDiscount(price) {
      if (!this.coupon) return 0;
      let discount = 0;
      if (this.coupon.discount_type === 'fixed_amount') {
        discount = Math.min(this.coupon.discount_amount, price);
      } else if (this.coupon.discount_type === 'percentage') {
        discount = price * (this.coupon.discount_amount / 100);
      }
      return discount;
    },
    logout() {
      useUserStore().logout();
    },
    openLoginModal() {
      this.$modal.show(StudentLogin, {
        onLoginFn: () => {
          this.loadProduct();
        },
      });
    },
    openRegisterModal() {
      this.$modal.show(Signup, {
        onSignupFn: () => {
          // TODO anything ?
        },
        displayLoginLink: false,
      });
    },
    async applyCoupon() {
      const payload = {
        studioURL: this.studioURL,
        productId: this.productId,
        couponCode: this.couponCode,
      };
      this.isCheckingCoupon = true;
      this.coupon = null;
      this.couponError = null;
      try {
        this.coupon = await useProductStore().validateStudentCoupon(payload);
        if (
          this.isRecurring &&
          this.isPayPalProcessor &&
          this.coupon.duration_type !== 'unlimited'
        ) {
          this.couponError = this.$t('PRODUCTS.TEXT.PAYPAL_COUPON_ERROR');
          this.coupon = null;
        }
        if (this.coupon) {
          this.$notify({
            group: 'app',
            type: 'success',
            title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
            text: this.$t('NOTIFICATIONS.TEXT.COUPON_SUCCESSFULLY_APPLIED'),
          });
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to check coupon code',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isCheckingCoupon = false;
      }
    },
    stripeChangeCallback(event) {
      if (event.complete) {
        this.stripeCompleted = true;
        this.stripeError = false;
      } else if (event.error) {
        this.stripeCompleted = false;
        this.stripeError = event.error.message;
      }
    },
    async loadProduct() {
      const studioURL = this.studioURL;
      const productId = this.productId;
      this.loading = true;
      try {
        this.product = await useProductStore().getProductDetail({
          studioURL,
          productId,
        });
        this.productForPurchase = await useProductStore().getProductForPurchase({
          studioURL,
          productId,
        });
        this.useExistingCustomerData = !!this.existingCreditCard;
        if (!this.selectedPriceOption) {
          this.selectedPriceOption = this.priceOptions[0].value;
          if (this.getPaymentPlanActivationCode) {
            // if we have a payment plan activation code, select the secret payment plan
            const paymentPlanOption = this.getPaymentPlanOption(
              this.getPaymentPlanActivationCode
            );
            if (paymentPlanOption) {
              this.selectedPriceOption = paymentPlanOption.value;
            }
          }
        } else {
          const selectedPriceOptionExists = this.priceOptions.find(
            o => o.value === this.selectedPriceOption
          );
          if (!selectedPriceOptionExists) {
            this.selectedPriceOption = this.priceOptions[0].value;
          }
        }
        if (this.$route.query.daypass) {
          const dayPassOption = this.priceOptions.find(o => o.isDaypass);
          if (dayPassOption) {
            this.selectedPriceOption = dayPassOption.value;
          }
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: `Failed to load product ${this.productId}`,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.loading = false;
      }
    },
    formatPayPalPrice(price) {
      return price.toFixed(2);
    },
    async processURLParameters() {
      if (this.$route.query.selectedPriceOption) {
        console.log('Found selectedPriceOption');
        this.selectedPriceOption = this.$route.query.selectedPriceOption;
      }
      this.$nextTick(async () => {
        if (this.$route.query.wishPrice) {
          console.log('Found wishPrice');
          this.wishPrice = this.$route.query.wishPrice;
          if (this.canApplyWishPrice) {
            this.applyWishPrice();
          }
        }
        if (this.$route.query.coupon) {
          console.log('Found coupon');
          this.couponCode = this.$route.query.coupon;
          if (this.canShowCouponCode) {
            this.applyCoupon();
          }
        }
      });
    },
    loadScripts() {
      loadDeadlineFunnelScript(
        this.productForPurchase.deadline_funnel_key,
        true
      );
    },
    applyWishPrice() {
      if (this.wishPrice) {
        this.wishPriceSelected = Number(this.wishPrice);
      } else this.wishPriceSelected = null;
    },
    getStudentDataForm() {
      return new Promise((resolve, reject) => {
        const interval = setInterval(
          () => {
            const validator = this.$refs?.studentDataForm;
            if (validator) {
              resolve(validator);
              clearInterval(interval);
            }
          },
          200,
          50
        );
      });
    },
    updateStudentInfo() {
      const data = { student: this.studentData };
      useUserStore().updateStudentDirect(data);
    },
    updateStudentInfoDebounced: _.debounce(
      function () {
        this.updateStudentInfo();
      },
      700,
      { leading: false, trailing: true }
    ),
  },
  watch: {
    selectedPriceOption() {
      this.wishPriceSelected = undefined;
      this.wishPrice = undefined;
    },
    studentData: {
      async handler(studentData) {
        if (!process.client) return;
        const validator = await this.getStudentDataForm();
        if (!validator) return;
        this.studentDataValid = await validator.validate();
        if (
          this.isPayPalProcessor &&
          this.studentDataValid &&
          validator.flags?.dirty
        ) {
          // for paypal save the data as it's entered
          this.updateStudentInfoDebounced();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await this.loadProduct();
    await this.processURLParameters();
    this.loadScripts();
    if (this.eventId) {
      Cookies.set('checkouteventid', this.eventId, { expires: 1 });
    } else {
      Cookies.remove('checkouteventid');
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

.studio-product-checkout {
}

// Optional per-product line under the checkout title. Layout only — no
// font-size is set here so a studio's custom `.checkout-subtitle` CSS (injected
// via their 3rd-party code customization) overrides cleanly. It inherits the
// default paragraph size, which is already smaller than the `<h2>` title.
.checkout-subtitle {
  margin-top: -0.75rem;
  margin-bottom: 1.5rem;
}

.price-option-container {
  padding: 1rem;
  border: 1px solid $medium-gray;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.img-paypal {
  height: 22px;
  width: auto;
}

.img-stripe {
  height: 22px;
  width: auto;
}

.img-cc {
  height: 40px;
  width: auto;

  @include media-breakpoint-down(xs) {
    height: 30px;
    width: auto;
  }
}

.full-height {
  min-height: 100vh;
}

.img-container {
  flex-shrink: 0;
  width: 150px;
  &.mobile {
    @include media-breakpoint-only(xs) {
      width: 100%;
    }
  }
}

.mobile-product-container {
  margin-left: -15px;
  margin-right: -15px;
}

.summary-section {
  padding: 0.5rem 1rem;
}

.checkout-button-container {
  @include media-breakpoint-only(xs) {
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: white;
    height: 70px;
    width: 100%;
    z-index: 1031;
    padding: 1rem 15px;
    box-shadow: 0px -1px 5px rgba(50, 50, 50, 0.2);
  }
}

.filler {
  height: 40px;
}

.bottom-section {
  padding-bottom: 4rem;
}
</style>

<style lang="scss">
@import '@/styles/components-variables.scss';

.price-line-through .c-charcoal {
  text-decoration: line-through;
}
#pay-what-you-wish::placeholder {
  @include media-breakpoint-down(sm) {
    font-size: 12px;
  }
}
</style>
